/**
 * Ranks catalogue entries against what a Team member typed. A seam: real users
 * get the in-browser embedding model, tests get the word-overlap matcher.
 */
export interface QuestionMatcher {
  ready: Promise<void>;
  /** `ready` has already resolved, so the button can appear on this render. */
  loaded?: boolean;
  rank(
    text: string,
    allowedIds: string[]
  ): Promise<RankedEntry[]>;
  /**
   * `rank`, for several (text, allowedIds) pairs at once, in the order given: lets a
   * matcher that embeds text compute each distinct text once and reuse it across every
   * ranking that asked for it, rather than once per pair.
   */
  rankMany(requests: RankRequest[]): Promise<RankedEntry[][]>;
  /**
   * Scores `text` against free phrases, on this matcher's own scale. Used for a
   * short follow-up that is not one of the exact phrases. Optional: a matcher
   * without it only accepts those exact phrases.
   */
  scorePhrases?(text: string, phrases: string[]): Promise<number[]>;
  /** What this matcher's scores mean. Each matcher scores on its own scale. */
  thresholds: MatchThresholds;
}

export type RankRequest = { text: string; allowedIds: string[] };

/**
 * A catalogue entry's score for one message; higher is a better match. `exact` when
 * the message is one of the entry's example phrasings, word for word.
 */
export type RankedEntry = { id: string; score: number; exact?: true };

/** A phrasing as compared word for word: no case, punctuation or extra spaces. */
export const phrasingKey = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** The ids of the entries with an example phrasing of each key. */
export function idsByPhrasing(entries: MatchableEntry[]): Map<string, Set<string>> {
  const ids = new Map<string, Set<string>>();
  entries.forEach(({ id, examples }) =>
    examples.forEach((example) => {
      const key = phrasingKey(example);
      ids.set(key, (ids.get(key) ?? new Set()).add(id));
    })
  );
  return ids;
}

/** What a matcher needs from each catalogue entry. */
export type MatchableEntry = { id: string; examples: string[] };

/** Scores the conversation acts on, from best to worst. */
export type MatchThresholds = {
  /** At or above this, and `clearLead` ahead of the next entry, the top entry is answered. */
  answer: number;
  /** How far ahead of the next entry the top one must be to answer without asking. */
  clearLead: number;
  /** At or above this, an entry is offered as "Did you mean…". Keep it at most `answer - clearLead`, so a close runner-up is offered. */
  didYouMean: number;
  /** At or above this, an entry counts as related ("closest questions"). */
  related: number;
};

/** How sure the best score was, on this matcher's own scale. */
export type MatchConfidence = "confident" | "unsure" | "lost";

/**
 * `confident` at or above the answer threshold, `unsure` at or above "did you mean",
 * otherwise `lost`. Word-overlap and embedding scores use different thresholds.
 */
export function confidenceFor(score: number, thresholds: MatchThresholds): MatchConfidence {
  if (score >= thresholds.answer) return "confident";
  if (score >= thresholds.didYouMean) return "unsure";
  return "lost";
}
